import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  formatTimeZoneLabel,
  getReportingOverride,
  getReportingTimeZone,
  setReportingOverride,
  subscribeReportingTimeZone,
} from '@/lib/report-timezone';

/**
 * Reactive access to the active reporting timezone. Components re-render when
 * the override changes; changing it also invalidates POS report queries so
 * every report refetches with the new zone (the zone is applied inside the
 * query layer's `withLocalDateRange`, so the query keys themselves don't carry
 * it — invalidation is what forces the refetch).
 */
export function useReportingTimeZone() {
  const qc = useQueryClient();
  const timeZone = useSyncExternalStore(subscribeReportingTimeZone, getReportingTimeZone, getReportingTimeZone);
  const override = useSyncExternalStore(subscribeReportingTimeZone, getReportingOverride, getReportingOverride);

  const setOverride = useCallback(
    (tz: string | null) => {
      setReportingOverride(tz);
      // Refetch every POS report (keys are strings prefixed with "pos").
      qc.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('pos'),
      });
    },
    [qc],
  );

  return { timeZone, override, setOverride, label: formatTimeZoneLabel(timeZone) };
}
